import { Stack } from "@mui/material";
import { Table } from "components";
import { LoadingOrEmptyWrapper } from "components/LoadingAndEmptyHandler";
import { DynamicRecordDrawer } from "features/shared/components/dynamic-form";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { LayeredConstructionApi } from "../api/createLayeredConstructionApi";
import type { LayeredConstructionVariant } from "../model/variants";

import { mapCatalogToTableData } from "../lib/catalogTableData";
import { getLayerPoolTableHeaders, getTypePoolTableHeaders } from "../lib/catalogTableHeaders";

type CatalogDrawerProps = {
  api: LayeredConstructionApi;
  onClose: () => void;
  open: boolean;
  variant: LayeredConstructionVariant;
};

export const CatalogDrawer = ({ api, onClose, open, variant }: CatalogDrawerProps) => {
  const { t } = useTranslation();

  const { data: catalog = [], isError, isLoading } = api.useGetCatalog(open);

  const tableData = useMemo(() => mapCatalogToTableData(catalog), [catalog]);
  const parentHeaders = useMemo(() => getTypePoolTableHeaders(t, variant), [t, variant]);
  const childHeaders = useMemo(() => getLayerPoolTableHeaders(t, variant), [t, variant]);

  const i18nPrefix = `TechnicalData.${variant.i18nKey}`;

  return (
    <DynamicRecordDrawer
      hideFooter
      onClose={onClose}
      open={open}
      title={String(t(`${i18nPrefix}.catalogTitle`))}
      widthClassName="w-[70rem]"
    >
      <Stack className="h-full w-full flex-col items-stretch gap-4">
        {isError && (
          <p className="font-ds-body-regular-sm text-lds-purpose-status-negative-subtle">
            {String(t(`${i18nPrefix}.catalogLoadError`))}
          </p>
        )}
        <LoadingOrEmptyWrapper showEmpty={tableData.length === 0} showLoading={isLoading}>
          <Stack className="h-full w-full flex-col overflow-hidden">
            <Table
              childEditable={() => false}
              childHeaders={childHeaders}
              childTableDataKey="layers"
              className="flex-1"
              columnsMinWidth="9rem"
              containedScroll
              data={tableData}
              editable={() => false}
              headers={parentHeaders}
              idColumnName="id"
              rowRemoveable={() => false}
            />
          </Stack>
        </LoadingOrEmptyWrapper>
      </Stack>
    </DynamicRecordDrawer>
  );
};
